import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import './AddProtocol.css';

const PHASES = [
  { key: 'pre_insemination',  labelKey: 'phase_pre_insemination',  hintKey: 'phase_pre_insemination_hint' },
  { key: 'insemination',      labelKey: 'phase_insemination',      hintKey: 'phase_insemination_hint' },
  { key: 'post_insemination', labelKey: 'phase_post_insemination', hintKey: 'phase_post_insemination_hint' },
];

const SYSTEM_ACTIONS_BY_PHASE = {
  post_insemination: [
    { key: 'mark_all_pregnant', labelKey: 'system_action_mark_all_pregnant' },
    { key: 'notify_birthing_window', labelKey: 'system_action_notify_birthing_window' },
    { key: 'mark_lost_pregnancies', labelKey: 'system_action_mark_lost_pregnancies' },
    { key: 'mark_lot_archived', labelKey: 'system_action_mark_lot_archived' },
  ],
};

// Mirrors posso-help-corral-manager's shared.jsx TARGET_GROUP grouping — which
// existing action targets belong to THIS protocol's own domain, so "attach an
// existing action" doesn't offer Herd fields on a Reproduction protocol (and
// vice versa). The first entry in each list doubles as the group hint stamped
// on a brand-new custom action created from this screen, so it renders under
// the matching group in Build too, not always "Repro" regardless of planType.
const PLAN_TYPE_TARGETS = {
  iatf:    ['reproduction_result'],
  weight:  ['weight'],
  general: ['note', 'breed', 'sex', 'area', 'is_alive', 'death_cause'],
};

const normalizeTimeline = (timeline) => {
  if (!timeline || timeline.length === 0) {
    return [{ start_day: 0, end_day: 0, phase: 'pre_insemination', system_action: '', action_ids: [], note: '' }];
  }
  return timeline.map(day => ({
    start_day:     day.start_day || 0,
    end_day:       day.end_day ?? day.start_day ?? 0,
    phase:         day.phase || 'pre_insemination',
    system_action: day.system_action || '',
    action_ids:    (day.action_ids || []).map(id => id?.$oid || id),
    note:          day.note || '',
  }));
};

// Sentinel option value for "+ Add New Action" at the bottom of the dropdown —
// never a real action id, so it can't collide with one.
const CREATE_NEW_ACTION = '__create_new_action__';

// ActionBindings — every protocol step is composed of actions from the same
// corral.actions catalog Build/Fill use — a step only references existing
// actions, it never owns a private one, so the same action stays one stable,
// queryable identity everywhere it's used (Build, Fill, and here). Picking
// "+ Add New Action" from the same dropdown swaps it for a small inline form
// (label only, first pass deliberately offers no other input type — Mark's
// call: step by step) instead of a separate always-visible control.
const ActionBindings = ({ boundIds, availableActions, attachableActions, onAdd, onRemove, onCreate, placeholderKey = 'action_placeholder' }) => {
  const { t } = useTranslation();
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Chip labels look up against the FULL catalog (an already-bound action
  // must still render correctly even if it's outside this protocol's domain
  // filter, e.g. from before this filter existed) — only the dropdown's
  // "attach something new" options are restricted to this protocol's own domain.
  const boundActions = boundIds
    .map(id => availableActions.find(a => a._id === id))
    .filter(Boolean);
  const unboundActions = attachableActions.filter(a => !boundIds.includes(a._id));

  const handleSelect = (value) => {
    if (!value) return;
    if (value === CREATE_NEW_ACTION) {
      setShowCreateForm(true);
      return;
    }
    onAdd(value);
  };

  const handleCreate = () => {
    const label = newLabel.trim();
    if (!label || creating) return;
    setCreating(true);
    onCreate(label,
      () => { setNewLabel(''); setCreating(false); setShowCreateForm(false); },
      () => { setCreating(false); }
    );
  };

  const cancelCreate = () => {
    setNewLabel('');
    setShowCreateForm(false);
  };

  return (
    <div className="action-bindings">
      {boundActions.length > 0 && (
        <div className="action-binding-chips">
          {boundActions.map(a => (
            <span key={a._id} className="action-binding-chip">
              {a.label}
              <button type="button" className="remove-action-binding-btn" onClick={() => onRemove(a._id)}>
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {!showCreateForm ? (
        <select value="" onChange={(e) => handleSelect(e.target.value)}>
          <option value="">{t('attach_action')}</option>
          {unboundActions.map(a => (
            <option key={a._id} value={a._id}>{a.label}</option>
          ))}
          <option value={CREATE_NEW_ACTION}>+ {t('create_action')}</option>
        </select>
      ) : (
        <div className="create-step-action">
          <input
            type="text"
            value={newLabel}
            placeholder={t(placeholderKey)}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleCreate(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelCreate(); }
            }}
            autoFocus
          />
          <button type="button" className="add-treatment-btn" onClick={handleCreate} disabled={creating || !newLabel.trim()}>
            {t('save')}
          </button>
          <button type="button" className="cancel-btn" onClick={cancelCreate}>
            {t('cancel')}
          </button>
        </div>
      )}
    </div>
  );
};

export const ProtocolForm = ({ initialData, mode = 'add', onSuccess, onCancel, planType = 'iatf' }) => {
  const { t } = useTranslation();
  const isEditMode  = mode === 'edit';
  const isIatf      = planType === 'iatf';

  const [name,           setName]           = useState('');
  const [description,    setDescription]    = useState('');
  const [notes,          setNotes]          = useState('');
  const [targetCows,     setTargetCows]     = useState(true);
  const [targetHeifers,  setTargetHeifers]  = useState(true);
  const [timelineDays,   setTimelineDays]   = useState([
    { start_day: 0, end_day: 0, phase: 'pre_insemination', system_action: '', action_ids: [], note: '' }
  ]);
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [error,          setError]          = useState(null);
  const [availableActions, setAvailableActions] = useState([]);

  const relevantTargets = PLAN_TYPE_TARGETS[planType] || PLAN_TYPE_TARGETS.iatf;
  const groupHint = relevantTargets[0];
  const domainActions = availableActions.filter(a => relevantTargets.includes(a.group || a.target));

  useEffect(() => {
    Fetch('api/corral/actions', 'get', null,
      (data) => setAvailableActions(data || []),
      () => {}
    );
  }, []);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setNotes(initialData.notes || '');
      setTargetCows(initialData.target_animals?.includes('Cows') ?? true);
      setTargetHeifers(initialData.target_animals?.includes('Heifers') ?? true);
      setTimelineDays(normalizeTimeline(initialData.timeline_days));
    }
  }, [initialData]);

  const addTimelineDay = (phase) => {
    const lastDay = timelineDays.length > 0
      ? Math.max(...timelineDays.map(t => t.end_day)) + 1
      : 0;
    setTimelineDays([...timelineDays, {
      start_day: lastDay, end_day: lastDay, phase, system_action: '', action_ids: [], note: '',
    }]);
  };

  const removeTimelineDay = (index) => {
    if (timelineDays.length > 1) {
      setTimelineDays(timelineDays.filter((_, i) => i !== index));
    }
  };

  const updateTimelineDay = (index, field, value) => {
    const updated = [...timelineDays];
    updated[index] = { ...updated[index], [field]: value };
    setTimelineDays(updated);
  };

  const addActionBinding = (dayIndex, actionId) => {
    const updated = [...timelineDays];
    const actionIds = updated[dayIndex].action_ids || [];
    if (!actionIds.includes(actionId)) {
      updated[dayIndex] = { ...updated[dayIndex], action_ids: [...actionIds, actionId] };
      setTimelineDays(updated);
    }
  };

  const removeActionBinding = (dayIndex, actionId) => {
    const updated = [...timelineDays];
    updated[dayIndex] = {
      ...updated[dayIndex],
      action_ids: (updated[dayIndex].action_ids || []).filter(id => id !== actionId),
    };
    setTimelineDays(updated);
  };

  // Creates a new yes/no corral.action, tagged with this protocol's own
  // domain (groupHint — reproduction_result/weight/note per planType) so
  // Build's field palette shows it under the matching group, not always
  // "Repro" regardless of which kind of protocol it was created from.
  // input_type=yes_no, not text: a protocol step's label ("Check for Ticks")
  // IS the instruction — the operator is marking whether they did it, not
  // writing a note about it. First pass deliberately hardcodes this one
  // type; richer types are a later iteration.
  const createStepAction = (dayIndex, label, onSuccess, onError) => {
    Fetch('api/corral/actions', 'post', JSON.stringify({
      label,
      input_type: 'yes_no',
      target: 'event',
      group: groupHint,
    }),
      (newAction) => {
        setAvailableActions(prev => [...prev, newAction]);
        addActionBinding(dayIndex, newAction._id);
        onSuccess();
      },
      () => { onError(); }
    );
  };

  // Soft nudge, not validation: sorts by end_day (falling back to start_day)
  // rather than trusting array order, since a step's day can be hand-edited
  // after addTimelineDay appended it — array-last and chronologically-last
  // can drift apart. Only meaningful for reproduction (isIatf) protocols;
  // herd/weight protocols have no system-action concept at all.
  const lastStepHasArchiveAction = useMemo(() => {
    if (timelineDays.length === 0) return true;
    const sorted = [...timelineDays].sort((a, b) => {
      const aDay = Number(a.end_day ?? a.start_day ?? 0);
      const bDay = Number(b.end_day ?? b.start_day ?? 0);
      return bDay - aDay;
    });
    return sorted[0]?.system_action === 'mark_lot_archived';
  }, [timelineDays]);

  const setSystemAction = (dayIndex, actionKey) => {
    updateTimelineDay(dayIndex, 'system_action', actionKey);
  };

  const clearSystemAction = (dayIndex) => {
    updateTimelineDay(dayIndex, 'system_action', '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const targetAnimals = [];
    if (targetCows)    targetAnimals.push('Cows');
    if (targetHeifers) targetAnimals.push('Heifers');

    const cleanedTimeline = timelineDays.map(day => {
      const step = {
        start_day:     parseInt(day.start_day) || 0,
        end_day:       parseInt(day.end_day) || parseInt(day.start_day) || 0,
        phase:         day.phase || 'pre_insemination',
      };
      if (day.system_action) step.system_action = day.system_action;
      if (day.action_ids && day.action_ids.length > 0) step.action_ids = day.action_ids;
      if (day.note && day.note.trim()) step.note = day.note.trim();
      return step;
    });

    const body = {
      name, description, notes,
      target_animals: targetAnimals,
      timeline_days:  cleanedTimeline,
      type:           initialData?.type || planType,
    };

    const url    = '/api/data/lots.plans';
    const method = isEditMode ? 'PATCH' : 'POST';
    const payload = isEditMode ? JSON.stringify({ _id: initialData._id, ...body }) : JSON.stringify(body);

    Fetch(url, method, payload,
      () => { setIsSubmitting(false); if (onSuccess) onSuccess(); },
      () => { setIsSubmitting(false); setError(t(isEditMode
        ? (isIatf ? 'error_updating_protocol' : 'error_updating_plan')
        : (isIatf ? 'error_creating_protocol' : 'error_creating_plan'))); }
    );
  };

  // Herd and weight plans share this form, so the heading names which kind
  // it is — "Add Plan" alone was easy to confuse between the two tabs.
  const titleKeys = {
    iatf:    ['add_protocol',    'edit_protocol'],
    general: ['add_herd_plan',   'edit_herd_plan'],
    weight:  ['add_weight_plan', 'edit_weight_plan'],
  }[planType] || ['add_plan', 'edit_plan'];
  const formTitle   = t(isEditMode ? titleKeys[1] : titleKeys[0]);
  // Herd/weight steps get their own note and action examples — the
  // reproduction ones ("Remove implant", mL for heifers) didn't fit them.
  const planKind = planType === 'weight' ? 'weight' : 'herd';
  const submitLabel = isEditMode
    ? (isSubmitting ? t('saving') : t(isIatf ? 'update_protocol' : 'update_plan'))
    : (isSubmitting ? t('saving') : t(isIatf ? 'save_protocol'   : 'save_plan'));

  const renderStep = (day, globalIndex, phaseKey) => {
    const availableSystemActions = SYSTEM_ACTIONS_BY_PHASE[phaseKey] || [];
    const hasSystemAction = !!day.system_action;

    return (
      <div key={globalIndex} className="timeline-day">
        <div className="day-header">
          <div className="day-inputs">
            <label>
              {t('day')}:
              <input
                type="number"
                min="0"
                value={day.start_day}
                onChange={(e) => updateTimelineDay(globalIndex, 'start_day', e.target.value)}
                className="day-input"
              />
            </label>
            <label>
              {t('to')}:
              <input
                type="number"
                min={day.start_day}
                value={day.end_day}
                onChange={(e) => updateTimelineDay(globalIndex, 'end_day', e.target.value)}
                className="day-input"
              />
            </label>
          </div>
          {timelineDays.length > 1 && (
            <button type="button" className="remove-btn" onClick={() => removeTimelineDay(globalIndex)}>
              &times;
            </button>
          )}
        </div>

        <div className="treatments-section">
          <ActionBindings
            boundIds={day.action_ids || []}
            availableActions={availableActions}
            attachableActions={domainActions}
            onAdd={(actionId) => addActionBinding(globalIndex, actionId)}
            onRemove={(actionId) => removeActionBinding(globalIndex, actionId)}
            onCreate={(label, onSuccess, onError) => createStepAction(globalIndex, label, onSuccess, onError)}
          />

          {hasSystemAction && (
            <div className="sa-tooltip-wrapper">
              <div className="system-action-badge">
                <span className="system-action-icon">⚙️</span>
                <span>{t(`system_action_${day.system_action}`)}</span>
                <button
                  type="button"
                  className="remove-system-action-btn"
                  onClick={() => clearSystemAction(globalIndex)}
                >
                  &times;
                </button>
              </div>
              <div className="sa-tooltip">
                <span className="sa-tooltip-title">{t(`system_action_${day.system_action}_tooltip_title`)}</span>
                <span className="sa-tooltip-body">{t(`system_action_${day.system_action}_tooltip`)}</span>
              </div>
            </div>
          )}

          <div className="action-buttons-row">
            {availableSystemActions.map(sa => (
              !hasSystemAction && (
                <div key={sa.key} className="sa-tooltip-wrapper">
                  <button
                    type="button"
                    className="add-system-action-btn"
                    onClick={() => setSystemAction(globalIndex, sa.key)}
                  >
                    ⚙️ {t(sa.labelKey)}
                  </button>
                  <div className="sa-tooltip">
                    <span className="sa-tooltip-title">{t(`system_action_${sa.key}_tooltip_title`)}</span>
                    <span className="sa-tooltip-body">{t(`system_action_${sa.key}_tooltip`)}</span>
                  </div>
                </div>
              )
            ))}
          </div>

          <div className="step-note-section">
            <label>{t('step_note')}</label>
            <textarea
              value={day.note || ''}
              onChange={(e) => updateTimelineDay(globalIndex, 'note', e.target.value)}
              placeholder={t('step_note_placeholder')}
              rows={2}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderPhaseSection = (phase) => {
    const phaseSteps = timelineDays
      .map((day, i) => ({ ...day, _globalIndex: i }))
      .filter(day => day.phase === phase.key);

    return (
      <div key={phase.key} className={`form-section phase-section phase-${phase.key}`}>
        <h3>{t(phase.labelKey)}</h3>
        <p className="phase-hint">{t(phase.hintKey)}</p>

        {phaseSteps.length === 0 && (
          <p className="phase-empty">{t('no_steps_in_phase')}</p>
        )}

        {phaseSteps.map(day => renderStep(day, day._globalIndex, phase.key))}

        <button type="button" className="add-day-btn" onClick={() => addTimelineDay(phase.key)}>
          + {t('add_step')}
        </button>
      </div>
    );
  };

  return (
    <div className="ProtocolForm">
      <h2>{formTitle}</h2>
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>{t('basic_info')}</h3>
          <div className="form-group">
            <label>{t('name')} *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>{t('description')}</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="form-group">
            <label>{t('notes')}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="form-group">
            <label>{t('target_animals')}</label>
            <div className="checkbox-group">
              <label>
                <input type="checkbox" checked={targetCows} onChange={(e) => setTargetCows(e.target.checked)} />
                {t('cows')}
              </label>
              <label>
                <input type="checkbox" checked={targetHeifers} onChange={(e) => setTargetHeifers(e.target.checked)} />
                {t('heifers')}
              </label>
            </div>
          </div>
        </div>

        {isIatf ? (
          PHASES.map(renderPhaseSection)
        ) : (
          <div className="form-section">
            <h3>{t('timeline')}</h3>
            {timelineDays.map((day, dayIndex) => (
              <div key={dayIndex} className="timeline-day">
                <div className="day-header">
                  <div className="day-inputs">
                    <label>
                      {t('day')}:
                      <input
                        type="number" min="0" value={day.start_day}
                        onChange={(e) => updateTimelineDay(dayIndex, 'start_day', e.target.value)}
                        className="day-input"
                      />
                    </label>
                    <label>
                      {t('to')}:
                      <input
                        type="number" min={day.start_day} value={day.end_day}
                        onChange={(e) => updateTimelineDay(dayIndex, 'end_day', e.target.value)}
                        className="day-input"
                      />
                    </label>
                  </div>
                  {timelineDays.length > 1 && (
                    <button type="button" className="remove-btn" onClick={() => removeTimelineDay(dayIndex)}>
                      &times;
                    </button>
                  )}
                </div>
                <div className="treatments-section">
                  <ActionBindings
                    boundIds={day.action_ids || []}
                    availableActions={availableActions}
                    attachableActions={domainActions}
                    onAdd={(actionId) => addActionBinding(dayIndex, actionId)}
                    onRemove={(actionId) => removeActionBinding(dayIndex, actionId)}
                    onCreate={(label, onSuccess, onError) => createStepAction(dayIndex, label, onSuccess, onError)}
                    placeholderKey={`action_placeholder_${planKind}`}
                  />
                  <div className="step-note-section">
                    <label>{t(`step_note_${planKind}`)}</label>
                    <textarea
                      value={day.note || ''}
                      onChange={(e) => updateTimelineDay(dayIndex, 'note', e.target.value)}
                      placeholder={t(`step_note_placeholder_${planKind}`)}
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="add-day-btn" onClick={() => addTimelineDay('pre_insemination')}>
              + {t('add_timeline_day')}
            </button>
          </div>
        )}

        {isIatf && !lastStepHasArchiveAction && (
          <div className="protocol-archive-nudge">
            ⚠️ {t('protocol_missing_archive_step_nudge')}
          </div>
        )}

        <div className="form-actions">
          {isEditMode && onCancel && (
            <button type="button" className="cancel-btn" onClick={onCancel}>
              {t('cancel')}
            </button>
          )}
          <button type="submit" disabled={isSubmitting}>{submitLabel}</button>
        </div>
      </form>
    </div>
  );
};

export default ProtocolForm;
